import React, { FC } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { SelectTypes } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { FormatPlayerPosition, getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';

import { Player } from '../../types';

import {
    PlayerDetailsRow,
    PlayerHeader,
    PlayerNameSelectContainer,
    PlayerNameSelectHalfContainer,
    SmallGrayText,
    StyledSelect,
    SweaterHeader,
} from './styles';

type PlayerNameAndSelectProps = {
    player: Player;
    isModern?: boolean;
};

export const LargePlayerNameSelect: FC<PlayerNameAndSelectProps> = ({ player }) => {
    const intl = useIntl();
    const isWebview = useIsWebview();

    const navigateToNewPlayer = (playerId: SelectTypes.BasicOption) => {
        window.location.href = getPlayerHref(playerId.value, isWebview);
    };

    const playerSelectOptions = player?.currentTeamRoster?.map((teammate, i) => ({
        key: i,
        label: `${parseValueFromLocalizedAttribute(teammate.lastName)}, ${parseValueFromLocalizedAttribute(
            teammate.firstName
        )}`,
        value: teammate.playerId,
    }));

    const teamTriCode = window.__SITE_SETTINGS__.franchise.triCode.toUpperCase();

    return (
        <PlayerNameSelectContainer>
            <PlayerNameSelectHalfContainer>
                {/* NB: IDs for autotesting purposes - do not remove */}
                <h1 id="player-name">{`${parseValueFromLocalizedAttribute(
                    player.firstName
                )} ${parseValueFromLocalizedAttribute(player.lastName)}`}</h1>
                {player.teamLogo !== undefined && (
                    <TeamDisplay
                        logoUrlOverride={player.teamLogo}
                        team={{ triCode: teamTriCode }}
                        displayTricode={false}
                        displayLogo={true}
                        displayName={false}
                        className="logo"
                    />
                )}
                {player.sweaterNumber && (
                    <h1 role="none" id="player-sweater-number">
                        <FormattedMessage
                            {...CommonMessages.playerInfoDescriptions.long.number}
                            values={{ sweaterNumber: player.sweaterNumber }}
                        />
                    </h1>
                )}
                {player.position && (
                    <h1 role="none">
                        <FormatPlayerPosition position={player.position} id="player-position" />
                    </h1>
                )}
            </PlayerNameSelectHalfContainer>

            {playerSelectOptions && (
                <PlayerNameSelectHalfContainer>
                    <SmallGrayText htmlFor="player-select">
                        <FormattedMessage {...CommonMessages.tableHeaders.long.roster} />
                    </SmallGrayText>
                    <StyledSelect
                        dropdownButtonAriaLabel={intl.formatMessage({
                            defaultMessage: 'Expand Player Options',
                            description:
                                'Label for select element button, prompting user to expand player options in a dropdown menu',
                        })}
                        id="player-select"
                        onOptionChange={(_, value: SelectTypes.BasicOption) => navigateToNewPlayer(value)}
                        options={playerSelectOptions}
                        optionsBoxAriaLabel={intl.formatMessage({
                            defaultMessage: 'Player Options',
                            description:
                                'Label indicating that user is viewing a list of player options for a select component',
                        })}
                        selectedOption={
                            player ? playerSelectOptions.find((option) => option.value === player?.playerId) : null
                        }
                    />
                </PlayerNameSelectHalfContainer>
            )}
        </PlayerNameSelectContainer>
    );
};

export const SmallPlayerNameSelect: FC<PlayerNameAndSelectProps> = ({ player, isModern }) => {
    const teamTriCode = window.__SITE_SETTINGS__.franchise.triCode.toUpperCase();

    return (
        <PlayerNameSelectContainer $isModern={isModern}>
            {/* NB: IDs for autotesting purposes - do not remove */}
            <h1 id="player-name">{`${parseValueFromLocalizedAttribute(
                player.firstName
            )} ${parseValueFromLocalizedAttribute(player.lastName)}`}</h1>
            <PlayerDetailsRow>
                {player.teamLogo !== undefined && (
                    <TeamDisplay
                        logoUrlOverride={player.teamLogo}
                        team={{ triCode: teamTriCode }}
                        displayTricode={false}
                        displayLogo={true}
                        displayName={false}
                        className="logo"
                    />
                )}
                <SweaterHeader role="none" $teamLogo={player.teamLogo} id="player-sweater-number">
                    <FormattedMessage
                        {...CommonMessages.playerInfoDescriptions.long.number}
                        values={{ sweaterNumber: player.sweaterNumber }}
                    />
                </SweaterHeader>
                <PlayerHeader role="none">
                    <FormatPlayerPosition position={player.position} id="player-position" />
                </PlayerHeader>
            </PlayerDetailsRow>
        </PlayerNameSelectContainer>
    );
};
